import type {
  BodyShot,
  CaptionRequest,
  ChangePasswordRequest,
  CreateBodyShotRequest,
  CreateLookbookRequest,
  CreateRoomRequest,
  FeedQuery,
  Garment,
  GarmentQuery,
  HypeKind,
  Job,
  Look,
  Lookbook,
  Notification,
  Page,
  TryOnRequest,
  UpdateLookRequest,
  UpdateLookbookRequest,
  UpdateMeRequest,
  User,
} from '@racksavant/core';
import { applyHype } from '@racksavant/core';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { client } from './api';
import { useAuthStore } from '../store/auth';
import { toast } from '../store/toast';
import { errorMessage } from './errors';
import { groupByMood } from './mood';

/* ------------------------------------------------------------------ *
 * Keys
 * ------------------------------------------------------------------ */

export const qk = {
  me: ['me'] as const,
  bodyShots: ['bodyShots'] as const,
  garments: (query: Partial<GarmentQuery>) => ['garments', query] as const,
  garment: (id: string) => ['garment', id] as const,
  moodGarments: ['garments', 'byMood'] as const,
  drops: ['drops'] as const,
  look: (id: string) => ['look', id] as const,
  myLooks: ['looks', 'mine'] as const,
  feed: (scope: FeedQuery['scope'], mood?: string) => ['feed', scope, mood ?? null] as const,
  lookbooks: ['lookbooks'] as const,
  notifications: ['notifications'] as const,
  profile: (username: string) => ['profile', username] as const,
  room: (code: string) => ['room', code] as const,
  following: (userId: string) => ['following', userId] as const,
};

type ProfilePayload = Awaited<ReturnType<typeof client.profile>>;

/* ------------------------------------------------------------------ *
 * Cache surgery helpers — a Look lives in many lists at once.
 * ------------------------------------------------------------------ */

function mapInfinite(
  data: InfiniteData<Page<Look>> | undefined,
  lookId: string,
  updater: (look: Look) => Look,
): InfiniteData<Page<Look>> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => (item.id === lookId ? updater(item) : item)),
    })),
  };
}

export function updateLookEverywhere(
  queryClient: QueryClient,
  lookId: string,
  updater: (look: Look) => Look,
): void {
  queryClient.setQueriesData<Look>({ queryKey: ['look', lookId] }, (old) => (old ? updater(old) : old));
  queryClient.setQueriesData<InfiniteData<Page<Look>>>({ queryKey: ['feed'] }, (old) =>
    mapInfinite(old, lookId, updater),
  );
  queryClient.setQueriesData<InfiniteData<Page<Look>>>({ queryKey: ['looks', 'mine'] }, (old) =>
    mapInfinite(old, lookId, updater),
  );
  queryClient.setQueriesData<ProfilePayload>({ queryKey: ['profile'] }, (old) =>
    old ? { ...old, looks: old.looks.map((l) => (l.id === lookId ? updater(l) : l)) } : old,
  );
}

/**
 * The current viewer state of one look, from whichever cache happens to hold it.
 *
 * `applyHype` is a no-op when the look is already in the requested state, so the
 * optimistic update and its rollback are not symmetric: rolling back an update
 * that never happened removes a reaction the user genuinely has.
 */
function lookInCaches(queryClient: QueryClient, lookId: string): Look | null {
  const direct = queryClient.getQueryData<Look>(qk.look(lookId));
  if (direct) return direct;

  for (const [, data] of queryClient.getQueriesData<InfiniteData<Page<Look>>>({ queryKey: ['feed'] })) {
    const found = data?.pages.flatMap((page) => page.items).find((item) => item.id === lookId);
    if (found) return found;
  }
  for (const [, data] of queryClient.getQueriesData<InfiniteData<Page<Look>>>({
    queryKey: ['looks', 'mine'],
  })) {
    const found = data?.pages.flatMap((page) => page.items).find((item) => item.id === lookId);
    if (found) return found;
  }
  for (const [, data] of queryClient.getQueriesData<ProfilePayload>({ queryKey: ['profile'] })) {
    const found = data?.looks.find((item) => item.id === lookId);
    if (found) return found;
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * Me
 * ------------------------------------------------------------------ */

export function useMe(enabled = true) {
  const setUser = useAuthStore((s) => s.setUser);
  return useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      const user = await client.me();
      setUser(user);
      return user;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateMe() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: (input: UpdateMeRequest) => client.updateMe(input),
    onSuccess: (user: User) => {
      queryClient.setQueryData(qk.me, user);
      setUser(user);
      toast.good('Saved.');
    },
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

/**
 * The toast is the only confirmation: there is nothing to put in the cache,
 * and `me` is untouched by a password change.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordRequest) => client.changePassword(input),
    onSuccess: () => toast.good('Password changed. Every other session is signed out.'),
    // Handled at the call site so the wrong-current-password message can land
    // on the field it belongs to.
  });
}

/* ------------------------------------------------------------------ *
 * Body shots
 * ------------------------------------------------------------------ */

export function useBodyShots(enabled = true) {
  return useQuery({
    queryKey: qk.bodyShots,
    queryFn: () => client.listBodyShots(),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateBodyShot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBodyShotRequest) => client.createBodyShot(input),
    onSuccess: (shot: BodyShot) => {
      queryClient.setQueryData<BodyShot[]>(qk.bodyShots, (old) => [shot, ...(old ?? [])]);
      void queryClient.invalidateQueries({ queryKey: qk.bodyShots });
    },
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

export function useDeleteBodyShot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.deleteBodyShot(id),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<BodyShot[]>(qk.bodyShots, (old) => (old ?? []).filter((s) => s.id !== id));
      void queryClient.invalidateQueries({ queryKey: qk.myLooks });
      toast.show('Gone. For real.');
    },
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

export function useSetPrimaryBodyShot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.setPrimaryBodyShot(id),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<BodyShot[]>(qk.bodyShots, (old) =>
        (old ?? []).map((s) => ({ ...s, isPrimary: s.id === id })),
      );
    },
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

/* ------------------------------------------------------------------ *
 * Garments
 * ------------------------------------------------------------------ */

export function useGarments(query: Partial<GarmentQuery>) {
  return useInfiniteQuery({
    queryKey: qk.garments(query),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => client.garments({ ...query, cursor: pageParam }),
    getNextPageParam: (last: Page<Garment>) => (last.hasMore ? (last.cursor ?? undefined) : undefined),
    staleTime: 120_000,
  });
}

export function useGarment(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.garment(id ?? ''),
    queryFn: () => client.garment(id as string),
    enabled: Boolean(id),
    staleTime: 300_000,
  });
}

/**
 * The garments behind the mood tiles.
 *
 * One page, the largest the API will hand over, grouped by the moods each
 * garment declares. Every mood tile in the product reads from this one query,
 * so the rail on the Rack and the picker in a sheet never fetch twice.
 */
export function useMoodGarments() {
  return useQuery({
    queryKey: qk.moodGarments,
    queryFn: () => client.garments({ sort: 'trending', limit: 60 }),
    select: (page: Page<Garment>) => groupByMood(page.items),
    staleTime: 600_000,
  });
}

export function useDrops() {
  return useQuery({ queryKey: qk.drops, queryFn: () => client.drops(), staleTime: 300_000 });
}

/* ------------------------------------------------------------------ *
 * Looks
 * ------------------------------------------------------------------ */

export function useLook(id: string | undefined) {
  return useQuery({
    queryKey: qk.look(id ?? ''),
    queryFn: () => client.look(id as string),
    enabled: Boolean(id),
  });
}

export function useMyLooks() {
  return useInfiniteQuery({
    queryKey: qk.myLooks,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => client.myLooks({ cursor: pageParam }),
    getNextPageParam: (last: Page<Look>) => (last.hasMore ? (last.cursor ?? undefined) : undefined),
  });
}

export function useFeed(scope: FeedQuery['scope'], mood?: string) {
  return useInfiniteQuery({
    queryKey: qk.feed(scope, mood),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      client.feed({ scope, mood: mood as FeedQuery['mood'], cursor: pageParam }),
    getNextPageParam: (last: Page<Look>) => (last.hasMore ? (last.cursor ?? undefined) : undefined),
  });
}

export function useUpdateLook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateLookRequest }) => client.updateLook(id, input),
    onSuccess: (look: Look) => {
      queryClient.setQueryData(qk.look(look.id), look);
      updateLookEverywhere(queryClient, look.id, () => look);
      void queryClient.invalidateQueries({ queryKey: qk.lookbooks });
    },
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

export function useDeleteLook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.deleteLook(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: qk.look(id) });
      // A look lives in four caches (see `updateLookEverywhere`); purging only
      // "mine" left it on the Runway and on the profile grid after "Deleted."
      const dropFromPages = (old: InfiniteData<Page<Look>> | undefined) =>
        old
          ? {
              ...old,
              pages: old.pages.map((p) => ({ ...p, items: p.items.filter((l) => l.id !== id) })),
            }
          : old;
      queryClient.setQueriesData<InfiniteData<Page<Look>>>({ queryKey: ['looks', 'mine'] }, dropFromPages);
      queryClient.setQueriesData<InfiniteData<Page<Look>>>({ queryKey: ['feed'] }, dropFromPages);
      queryClient.setQueriesData<ProfilePayload>({ queryKey: ['profile'] }, (old) =>
        old ? { ...old, looks: old.looks.filter((l) => l.id !== id) } : old,
      );
      void queryClient.invalidateQueries({ queryKey: qk.lookbooks });
      toast.show('Deleted.');
    },
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

export function useHype() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ lookId, kind, on }: { lookId: string; kind: HypeKind; on: boolean }) =>
      client.hype(lookId, { kind, on }),
    onMutate: async ({ lookId, kind, on }) => {
      // Every cache `updateLookEverywhere` writes has to be cancelled, not just
      // the one. An in-flight feed fetch that resolves after this lands would
      // otherwise paint the pre-hype server state back over it.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: qk.look(lookId) }),
        queryClient.cancelQueries({ queryKey: ['feed'] }),
        queryClient.cancelQueries({ queryKey: ['looks', 'mine'] }),
        queryClient.cancelQueries({ queryKey: ['profile'] }),
      ]);
      const previous = queryClient.getQueryData<Look>(qk.look(lookId));
      const before = lookInCaches(queryClient, lookId);
      // False when the look was already in the requested state — then the
      // optimistic write did nothing and there is nothing to undo.
      const applied = before ? before.viewerHype.includes(kind) !== on : true;
      updateLookEverywhere(queryClient, lookId, (look) => applyHype(look, kind, on));
      return { previous, lookId, kind, on, applied };
    },
    onError: (error, _vars, context) => {
      if (context?.applied) {
        // Roll the optimistic reaction back everywhere it landed.
        updateLookEverywhere(queryClient, context.lookId, (look) =>
          applyHype(look, context.kind, !context.on),
        );
        if (context.previous) queryClient.setQueryData(qk.look(context.lookId), context.previous);
      }
      // The lists have no snapshot to restore, so let the server settle them.
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
      void queryClient.invalidateQueries({ queryKey: ['looks', 'mine'] });
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.bad(errorMessage(error));
    },
    onSuccess: (look: Look) => {
      updateLookEverywhere(queryClient, look.id, () => look);
    },
  });
}

export function useCaption() {
  return useMutation({
    mutationFn: (input: CaptionRequest) => client.caption(input),
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

/* ------------------------------------------------------------------ *
 * Try-on
 * ------------------------------------------------------------------ */

export function useTryOn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TryOnRequest) => client.createTryOn(input),
    onSuccess: (_job: Job) => {
      void queryClient.invalidateQueries({ queryKey: qk.me });
    },
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

/* ------------------------------------------------------------------ *
 * Lookbooks
 * ------------------------------------------------------------------ */

export function useLookbooks(enabled = true) {
  return useQuery({ queryKey: qk.lookbooks, queryFn: () => client.lookbooks(), enabled });
}

export function useCreateLookbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLookbookRequest) => client.createLookbook(input),
    onSuccess: (lookbook: Lookbook) => {
      queryClient.setQueryData<Lookbook[]>(qk.lookbooks, (old) => [lookbook, ...(old ?? [])]);
      toast.good(`"${lookbook.title}" started.`);
    },
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

export function useUpdateLookbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateLookbookRequest }) =>
      client.updateLookbook(id, input),
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: qk.lookbooks });
      const previous = queryClient.getQueryData<Lookbook[]>(qk.lookbooks);
      queryClient.setQueryData<Lookbook[]>(qk.lookbooks, (old) =>
        (old ?? []).map((book) => (book.id === id ? { ...book, ...input } : book)),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(qk.lookbooks, context.previous);
      toast.bad(errorMessage(error));
    },
    onSuccess: (lookbook: Lookbook) => {
      queryClient.setQueryData<Lookbook[]>(qk.lookbooks, (old) =>
        (old ?? []).map((book) => (book.id === lookbook.id ? lookbook : book)),
      );
    },
  });
}

export function useDeleteLookbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.deleteLookbook(id),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<Lookbook[]>(qk.lookbooks, (old) => (old ?? []).filter((b) => b.id !== id));
      toast.show('Era closed.');
    },
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

/* ------------------------------------------------------------------ *
 * Social
 * ------------------------------------------------------------------ */

export function useProfile(username: string | undefined) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: qk.profile(username ?? ''),
    queryFn: async () => {
      const payload = await client.profile(username as string);
      // The profile response is the only place the server tells us whether the
      // viewer already follows this person. Seed the follow cache from it, or
      // the button paints "Follow" for someone you followed months ago.
      queryClient.setQueryData(qk.following(payload.user.id), payload.following);
      return payload;
    },
    enabled: Boolean(username),
  });
}

export function useFollowState(userId: string | undefined) {
  return useQuery({
    queryKey: qk.following(userId ?? ''),
    queryFn: async () => false,
    enabled: false,
    initialData: false,
  });
}

export function useFollow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, on }: { userId: string; on: boolean; username?: string }) =>
      client.follow(userId, on),
    onMutate: async ({ userId, on, username }) => {
      // `useProfile` writes `qk.following` as a side effect, so cancelling only
      // the follow key let an in-flight profile fetch stamp the stale value back
      // and flip the button under the user's finger.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: qk.following(userId) }),
        username
          ? queryClient.cancelQueries({ queryKey: qk.profile(username) })
          : Promise.resolve(),
      ]);
      const previous = queryClient.getQueryData<boolean>(qk.following(userId)) ?? false;
      const previousProfile = username
        ? queryClient.getQueryData<ProfilePayload>(qk.profile(username))
        : undefined;
      queryClient.setQueryData(qk.following(userId), on);
      if (username) {
        queryClient.setQueryData<ProfilePayload>(qk.profile(username), (old) =>
          old
            ? {
                ...old,
                user: {
                  ...old.user,
                  counts: {
                    ...old.user.counts,
                    followers: Math.max(0, old.user.counts.followers + (on ? 1 : -1)),
                  },
                },
              }
            : old,
        );
      }
      return { previous, previousProfile, userId, username, on };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(qk.following(context.userId), context.previous);
        // Restore the follower count rather than only marking it stale — an
        // inactive profile query would keep rendering the optimistic number.
        if (context.username && context.previousProfile) {
          queryClient.setQueryData(qk.profile(context.username), context.previousProfile);
        } else if (context.username) {
          void queryClient.invalidateQueries({ queryKey: qk.profile(context.username) });
        }
      }
      toast.bad(errorMessage(error));
    },
    onSuccess: (data, vars) => {
      queryClient.setQueryData(qk.following(vars.userId), data.following);
      // Prefix key on purpose: `qk.feed(scope, mood)` is fully specified, so
      // passing it here left every mood-filtered following feed stale.
      void queryClient.invalidateQueries({ queryKey: ['feed', 'following'] });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Notifications
 * ------------------------------------------------------------------ */

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: qk.notifications,
    queryFn: () => client.notifications(),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.markNotificationsRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: qk.notifications });
      const previous = queryClient.getQueryData<Notification[]>(qk.notifications);
      queryClient.setQueryData<Notification[]>(qk.notifications, (old) =>
        (old ?? []).map((n) => ({ ...n, read: true })),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(qk.notifications, context.previous);
    },
  });
}

/* ------------------------------------------------------------------ *
 * Rooms
 * ------------------------------------------------------------------ */

export function useRoom(code: string | undefined) {
  return useQuery({
    queryKey: qk.room(code ?? ''),
    queryFn: () => client.room(code as string),
    enabled: Boolean(code),
  });
}

export function useCreateRoom() {
  return useMutation({
    mutationFn: (input: CreateRoomRequest) => client.createRoom(input),
    onError: (error) => toast.bad(errorMessage(error)),
  });
}

export function useJoinRoom() {
  return useMutation({
    mutationFn: (code: string) => client.joinRoom(code.trim().toUpperCase()),
    onError: (error) => toast.bad(errorMessage(error)),
  });
}
